import * as _ from 'lodash-es';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import EmptyState from '../components/EmptyState';
import ExternalPostPlatformIcon from '../components/ExternalPostPlatformIcon';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PageTitle from '../components/PageTitle';
import PostSortMenuButton from '../components/PostSortMenuButton';
import { gql } from '../graphql/generated/gql';
import type { PostPlatform } from '../graphql/generated/graphql';
import { PostFilter, PostSort } from '../graphql/generated/graphql';
import PageMeta from '../utils/PageMeta';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import { externalDisplayName } from '../utils/post/externalPost';
import * as styles from './ExternalPostsView.module.scss';
import PostRow from './PostRow';
import PostRowMeta from './PostRowMeta';
import PostSectionHeader from './PostSectionHeader';

const EXTERNAL_POSTS_QUERY = gql(/** @complexity 4 */ `
  query externalPosts(
    $platform: PostPlatform!
    $sortBy: PostSort
    $filters: [PostFilter!]
    $afterCursor: String
  ) {
    listPosts(
      first: 20,
      after: $afterCursor
      filters: $filters
      sortBy: $sortBy
      platform: $platform
    ) {
      edges {
        node {
          id
          views
          insertedAt
          editedAt
          ...PostRow_post
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const POST_SORT_OPTIONS = [
  PostSort.Popularity,
  PostSort.Edited,
  PostSort.Created,
  PostSort.Alphabetical,
] satisfies PostSort[];

const METADATA_ATTRIBUTE = {
  [PostSort.Popularity]: 'popularity',
  [PostSort.Edited]: 'editedAt',
  [PostSort.Created]: 'insertedAt',
  [PostSort.Alphabetical]: 'editedAt',
} as const;

const ExternalPostsView = () => {
  const { platform } = useParams() as { platform: string };
  const [isArchivedIncluded, setIsArchivedIncluded] = useState(false);
  const [sortBy, setSortBy] = useState<(typeof POST_SORT_OPTIONS)[number]>(
    PostSort.Popularity,
  );

  const response = useQuery(EXTERNAL_POSTS_QUERY, {
    variables: {
      platform: platform.toUpperCase() as PostPlatform,
      sortBy,
      filters: isArchivedIncluded ? [] : [PostFilter.Unarchived],
    },
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    EXTERNAL_POSTS_QUERY,
    response,
    'listPosts',
  );

  const displayName = externalDisplayName(platform);
  const docTitle = displayName ? `${displayName} - Slab` : 'Slab';

  const posts = _.compact(
    response.data?.listPosts?.edges?.map((edge) => edge?.node),
  );

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title={docTitle} />
      <div className={styles.container}>
        <TwoColumnRow
          main={
            <PageTitle
              title={
                <h1 className={styles.mainTitle}>
                  <div className={styles.titleLogo}>
                    <ExternalPostPlatformIcon id={platform} size={32} />
                  </div>
                  {displayName}
                </h1>
              }
              subtitle={`These posts are synced from Markdown files in your ${displayName} repositories.`}
            />
          }
        />
        <TwoColumnRow
          full={
            <>
              <PostSectionHeader
                title="Synced posts"
                controls={
                  <PostSortMenuButton
                    allowedPostSorts={POST_SORT_OPTIONS}
                    sortBy={sortBy}
                    onSortByChange={setSortBy}
                    isArchivedIncluded={isArchivedIncluded}
                    onIsArchivedIncludedChange={setIsArchivedIncluded}
                  />
                }
              />
              {posts.length ? (
                <PostRow.Provider>
                  {posts.map((post) => (
                    <PostRow
                      key={post.id}
                      post={post}
                      meta={
                        <PostRowMeta
                          attribute={METADATA_ATTRIBUTE[sortBy]}
                          values={{
                            editedAt: post.editedAt,
                            insertedAt: post.insertedAt,
                            popularity: post.views,
                          }}
                        />
                      }
                    />
                  ))}
                  <InfiniteScrollWaypoint fetchMore={fetchMore} />
                </PostRow.Provider>
              ) : (
                <EmptyState
                  imageName="notFound"
                  title="No posts found"
                  message={`We couldn't find viewable posts synced from ${displayName}.`}
                />
              )}
            </>
          }
        />
      </div>
    </PageLayout.Content>
  );
};

export default ExternalPostsView;
